// ─────────────────────────────────────────────────────────────────────────────
//  DiagnosticReferralSettings — "Diagnostic Referral Link"
//
//  Only a few facilities refer patients to a partner diagnostic centre, so the
//  link is switched on per facility here. OFF by default: until an admin turns
//  it on, doctors never see "Refer to Diagnostic" and nothing is sent.
//
//  The secret is write-only. The server never sends it back — the card shows
//  only whether one is set and its last four characters. Leaving the box
//  blank on save keeps the current secret.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControlLabel, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import {
  SaveOutlined as SaveIcon, WifiTetheringOutlined as TestIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import { diagnosticReferralSettings, diagnosticReferralTest } from '../BaseURL';

const EMPTY = { enabled: false, apiUrl: '', partnerCode: '', name: '', secret: '', facilityCode: '' };

const DiagnosticReferralSettings = () => {
  const [form, setForm]       = useState(EMPTY);
  const [saved, setSaved]     = useState(EMPTY);
  const [secretHint, setHint] = useState('');
  const [ownName, setOwnName] = useState('');   // this facility's name, to catch it typed as the partner
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult]   = useState(null);   // { ok, message } from Test
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(diagnosticReferralSettings);
      const next = {
        enabled: !!r.data.enabled,
        apiUrl: r.data.apiUrl || '',
        partnerCode: r.data.partnerCode || '',
        name: r.data.name === 'Diagnostic Centre' ? '' : (r.data.name || ''),
        secret: '',
        facilityCode: r.data.facilityCode || '',
      };
      setOwnName(r.data.facilityName || '');
      setForm(next);
      setSaved(next);
      setHint(r.data.hasSecret ? r.data.secretHint : '');
    } catch (err) {
      setError(err.response?.status === 404
        ? 'This server does not have the referral feature yet — deploy the latest backend.'
        : (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: k === 'enabled' ? e.target.checked : e.target.value }));
    setResult(null);
  };
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(diagnosticReferralSettings, form);
      await load();
      await Swal.fire({
        icon: r.data.warning ? 'warning' : 'success',
        title: r.data.warning ? 'Saved — not active yet' : 'Saved',
        text: r.data.warning || (form.enabled
          ? 'Doctors will see "Refer to Diagnostic" within 30 seconds.'
          : 'The referral button is hidden for this facility.'),
        timer: r.data.warning ? undefined : 2400,
        showConfirmButton: !!r.data.warning,
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await axios.post(diagnosticReferralTest);
      setResult({ ok: !!r.data.success, message: r.data.message });
    } catch (err) {
      setResult({ ok: false, message: err.response?.data?.message || err.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={26} /></Box>;
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>{error}</Alert>}

      <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>
        Lets doctors send a patient's labs and scans to a partner diagnostic centre with no payment
        here. The diagnostic centre registers the patient, generates the CCC and runs the tests.
        Only switch this on at facilities that have an agreement with that centre.
      </Typography>

      <Box sx={{
        border: '1px solid', borderRadius: 2, p: 1.5,
        borderColor: form.enabled ? '#11998e' : '#e2e8f0',
        bgcolor: form.enabled ? 'rgba(17,153,142,0.04)' : '#fff',
      }}>
        <FormControlLabel
          control={<Switch checked={form.enabled} onChange={set('enabled')} color="success" />}
          label={
            <Box>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700 }}>
                {form.enabled ? 'Referral link is ON for this facility' : 'Referral link is OFF for this facility'}
              </Typography>
              <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                {form.enabled
                  ? 'Doctors see "Refer to Diagnostic" on the consulting screen.'
                  : 'Doctors do not see the button and nothing is sent.'}
              </Typography>
            </Box>
          }
        />
      </Box>

      {/* THIS facility — goes on every referral as the referring facility */}
      <TextField size="small" fullWidth required label="This facility's NHIS facility code"
        placeholder="The code NHIA issued to this hospital/clinic"
        error={!form.facilityCode.trim()}
        helperText={form.facilityCode.trim()
          ? `Sent as the referring facility code${ownName ? ` for ${ownName}` : ''}. Also used by claims.`
          : 'Required — referrals are refused without it.'}
        value={form.facilityCode} onChange={set('facilityCode')} />

      {ownName && form.name.trim() && form.name.trim().toLowerCase() === ownName.trim().toLowerCase() && (
        <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>
          The diagnostic centre's name is the same as this facility's name ({ownName}). Enter the name of the
          diagnostic centre you refer TO, and check the address below points to its server, not this one.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField size="small" fullWidth label="Diagnostic centre name (shown to doctors)"
          placeholder="e.g. Delab Diagnostic Centre" value={form.name} onChange={set('name')} />
        <TextField size="small" fullWidth label="Our partner code there"
          placeholder="e.g. DELAB-HCP" value={form.partnerCode} onChange={set('partnerCode')} />
      </Stack>

      <TextField size="small" fullWidth label="Diagnostic backend address"
        placeholder="https://xxxdb.yourcaresystem.online"
        helperText="The diagnostic system's backend (API) address — the URL in its BaseURL.js, not its website."
        value={form.apiUrl} onChange={set('apiUrl')} />

      <TextField size="small" fullWidth type="password" autoComplete="new-password"
        label={secretHint ? `Shared secret (set, ends ${secretHint}) — leave blank to keep` : 'Shared secret'}
        helperText="Must be identical to the secret the diagnostic centre saved for this partner code."
        value={form.secret} onChange={set('secret')} />

      {result && (
        <Alert severity={result.ok ? 'success' : 'error'} sx={{ fontSize: '0.82rem' }}>{result.message}</Alert>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button variant="outlined" onClick={test} disabled={testing || dirty}
          title={dirty ? 'Save first, then test' : ''}
          startIcon={testing ? <CircularProgress size={15} /> : <TestIcon />}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {testing ? 'Testing…' : 'Test connection'}
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !dirty}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, background: '#11998e', '&:hover': { background: '#0e8a7f' } }}>
          {saving ? 'Saving…' : dirty ? 'Save change' : 'Saved'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default DiagnosticReferralSettings;
